import React, { useState, useEffect, useCallback } from 'react';
import {
  Calendar, Clock, CheckCircle, AlertCircle, Filter,
  Search, Briefcase, Flag, Paperclip, Loader2, X,
  ListTodo, GitPullRequest, RotateCcw,
} from 'lucide-react';
import { format } from 'date-fns';
import { useApiAuth } from '../../../contexts/ApiAuthContext';
import { projectBoardService } from '../api/boardApi';
import { taskService } from '..';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { useActivityLog } from '../../../contexts/ActivityLogContext';
import { BRAND_COLORS, SYSTEM_COLORS, NEUTRAL_COLORS } from '../../../constants/brandPalette';

// ── Helpers ───────────────────────────────────────────────────────────────────

const normalizeTaskList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.tasks)) return payload.tasks;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const fmtDate = (ts) => {
  if (!ts) return null;
  try { return format(new Date(ts), 'MMM d, yyyy'); }
  catch { return null; }
};

const STATUS_META = {
  todo:       { label: 'To Do',       bg: '#f3f4f6', color: '#4b5563' },
  inProgress: { label: 'In Progress', bg: SYSTEM_COLORS.primaryTint,  color: BRAND_COLORS.primary },
  review:     { label: 'Review',      bg: '#fdf4ff', color: '#7e22ce' },
  done:       { label: 'Done',        bg: '#dcfce7', color: '#15803d' },
  dispose:    { label: 'Disposed',    bg: '#fee2e2', color: '#b91c1c' },
};

const PRIORITY_META = {
  high:   { label: 'High',   bg: '#fee2e2', color: '#b91c1c' },
  medium: { label: 'Medium', bg: '#fef3c7', color: '#b45309' },
  low:    { label: 'Low',    bg: '#f0fdf4', color: '#166534' },
};

// ── TaskCard ──────────────────────────────────────────────────────────────────

const TaskCard = ({ task, onStatusChange }) => {
  const sm = STATUS_META[task.status] || STATUS_META.todo;
  const pm = task.priority ? PRIORITY_META[task.priority] : null;
  const dueDate = fmtDate(task.dueDate);
  const createdDate = fmtDate(task.createdAt);

  let dueDateEl = null;
  if (dueDate) {
    const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'done';
    dueDateEl = (
      <span className={isOverdue ? 'text-red-600 font-medium' : ''}>
        {dueDate}{isOverdue ? ' · Overdue' : ''}
      </span>
    );
  }

  return (
    <div
      className="group flex flex-col rounded-xl border overflow-hidden transition-all duration-150 hover:-translate-y-0.5"
      style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}
    >

      {/* Priority accent bar */}
      <div className="h-1 flex-shrink-0"
        style={{ backgroundColor: pm ? pm.color : (sm.color || BRAND_COLORS.primary) }} />

      <div className="flex flex-col flex-1 p-4 gap-3">

        {/* Title + status */}
        <div className="flex items-start justify-between gap-2">
          <h3
            className="text-sm font-semibold leading-snug transition-colors line-clamp-2 flex-1"
            style={{ color: NEUTRAL_COLORS.black }}
          >
            {task.title}
          </h3>
          <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold"
            style={{ backgroundColor: sm.bg, color: sm.color }}>
            {sm.label}
          </span>
        </div>

        {/* Description */}
        {task.description && (
          <p className="text-xs line-clamp-2 leading-relaxed" style={{ color: SYSTEM_COLORS.textMuted }}>{task.description}</p>
        )}

        {/* Meta rows */}
        <div className="space-y-1.5 text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
          {task.projectName && (
            <div className="flex items-center gap-1.5">
              <Briefcase size={11} className="flex-shrink-0" style={{ color: SYSTEM_COLORS.textSoft }} />
              <Link to={`/projects/${task.projectId}`}
                className="truncate hover:underline"
                style={{ color: BRAND_COLORS.primary }}>
                {task.projectName}
              </Link>
            </div>
          )}

          {task.milestoneId && task.milestoneName && (
            <div className="flex items-center gap-1.5">
              <Flag size={11} className="flex-shrink-0" style={{ color: SYSTEM_COLORS.textSoft }} />
              <span className="truncate">{task.milestoneName}</span>
            </div>
          )}

          {dueDate && (
            <div className="flex items-center gap-1.5">
              <Calendar size={11} className="flex-shrink-0" style={{ color: SYSTEM_COLORS.textSoft }} />
              {dueDateEl}
            </div>
          )}

          {createdDate && (
            <div className="flex items-center gap-1.5">
              <Clock size={11} className="flex-shrink-0" style={{ color: SYSTEM_COLORS.textSoft }} />
              <span>Created {createdDate}</span>
            </div>
          )}
        </div>

        {/* Badges row */}
        {pm && (
          <div className="flex flex-wrap gap-1.5">
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
              style={{ backgroundColor: pm.bg, color: pm.color }}>
              <AlertCircle size={9} />
              {pm.label} Priority
            </span>
          </div>
        )}

        {/* Attachments */}
        {task.evidence?.length > 0 && (
          <div className="flex items-center gap-1.5 text-xs pt-1 border-t"
            style={{ color: SYSTEM_COLORS.textSoft, borderColor: SYSTEM_COLORS.borderSoft }}>
            <Paperclip size={11} />
            <span>{task.evidence.length} attachment{task.evidence.length !== 1 ? 's' : ''}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Completed task row ────────────────────────────────────────────────────────

const CompletedRow = ({ task }) => (
  <div className="flex items-start gap-3 px-4 py-3 rounded-xl border"
    style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
    <CheckCircle size={15} className="flex-shrink-0 mt-0.5" style={{ color: SYSTEM_COLORS.success }} />
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium truncate" style={{ color: NEUTRAL_COLORS.black }}>{task.title}</p>
      {task.projectName && (
        <p className="text-xs truncate mt-0.5" style={{ color: SYSTEM_COLORS.textSoft }}>{task.projectName}</p>
      )}
    </div>
    {task.updatedAt && (
      <span className="text-[10px] flex-shrink-0 mt-0.5" style={{ color: SYSTEM_COLORS.textSoft }}>{fmtDate(task.updatedAt)}</span>
    )}
  </div>
);

// ── Main Page ─────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: 'all',        label: 'All Tasks' },
  { value: 'todo',       label: 'To Do' },
  { value: 'inProgress', label: 'In Progress' },
  { value: 'review',     label: 'Review' },
];

const Tasks = () => {
  const { user } = useApiAuth();
  const { logTaskActivity } = useActivityLog();

  const [tasks, setTasks]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch]             = useState('');
  const [showDone, setShowDone]         = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const payload = await projectBoardService.getUserTasks();
      setTasks(normalizeTaskList(payload));
    } catch {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (projectId, taskId, newStatus) => {
    try {
      await taskService.updateTaskStatus(taskId, newStatus);
      let updated;
      setTasks(prev => prev.map(t => {
        if (t.id === taskId && t.projectId === projectId) {
          updated = { ...t, status: newStatus };
          return updated;
        }
        return t;
      }));
      if (updated) await logTaskActivity(newStatus === 'done' ? 'complete' : 'update', updated, projectId);
      toast.success('Status updated');
    } catch {
      toast.error('Failed to update status');
    }
  };

  const filtered = tasks.filter(t => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      t.title?.toLowerCase().includes(q) ||
      t.description?.toLowerCase().includes(q) ||
      t.projectName?.toLowerCase().includes(q);
    const matchStatus = statusFilter === 'all' || t.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const activeTasks    = filtered.filter(t => t.status !== 'done' && t.status !== 'dispose');
  const completedTasks = filtered.filter(t => t.status === 'done');

  const stats = [
    { label: 'Total',       value: tasks.length,                                               icon: ListTodo,      color: BRAND_COLORS.primary,   tint: SYSTEM_COLORS.primaryTint },
    { label: 'In Progress', value: tasks.filter(t => t.status === 'inProgress').length,        icon: RotateCcw,     color: BRAND_COLORS.primary,   tint: SYSTEM_COLORS.primaryTint },
    { label: 'In Review',   value: tasks.filter(t => t.status === 'review').length,            icon: GitPullRequest, color: '#7e22ce',              tint: '#fdf4ff' },
    { label: 'Done',        value: tasks.filter(t => t.status === 'done').length,              icon: CheckCircle,   color: SYSTEM_COLORS.success,  tint: SYSTEM_COLORS.successTint },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 gap-3">
        <Loader2 size={20} className="animate-spin" style={{ color: BRAND_COLORS.primary }} />
        <span className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>Loading tasks…</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {stats.map(({ label, value, icon: Icon, color, tint }) => (
          <div key={label}
            className="flex items-center gap-3 px-4 py-3.5 rounded-xl border"
            style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
            <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-xl"
              style={{ backgroundColor: tint }}>
              <Icon size={16} style={{ color }} />
            </div>
            <div>
              <p className="text-xl font-bold leading-none" style={{ color }}>{value}</p>
              <p className="text-[11px] mt-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="flex flex-1 items-center gap-2 rounded-xl border px-3 py-2.5"
          style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
          <Search size={15} className="flex-shrink-0" style={{ color: SYSTEM_COLORS.textSoft }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search tasks, projects…"
            className="flex-1 text-sm bg-transparent outline-none"
            style={{ color: NEUTRAL_COLORS.black }}
          />
          {search && (
            <button onClick={() => setSearch('')}>
              <X size={13} style={{ color: SYSTEM_COLORS.textSoft }} />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 rounded-xl border px-3 py-2.5 sm:w-44"
          style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
          <Filter size={13} className="flex-shrink-0" style={{ color: SYSTEM_COLORS.textSoft }} />
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="flex-1 text-sm bg-transparent outline-none"
          >
            {STATUS_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <button
          onClick={() => setShowDone(v => !v)}
          className="flex items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-medium transition-colors"
          style={{
            borderColor: showDone ? BRAND_COLORS.primary : SYSTEM_COLORS.borderSoft,
            backgroundColor: showDone ? SYSTEM_COLORS.primaryTint : SYSTEM_COLORS.shellSurface,
            color: showDone ? BRAND_COLORS.primary : '#6b7280',
          }}>
          <CheckCircle size={14} />
          Completed
        </button>
      </div>

      {/* Active tasks */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: SYSTEM_COLORS.textSoft }}>
            Active Tasks
          </p>
          <span className="text-xs" style={{ color: SYSTEM_COLORS.textSoft }}>{activeTasks.length} task{activeTasks.length !== 1 ? 's' : ''}</span>
        </div>

        {activeTasks.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 rounded-xl border"
            style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
            <div className="w-12 h-12 flex items-center justify-center rounded-lg"
              style={{ backgroundColor: SYSTEM_COLORS.shellSurfaceMuted }}>
              <CheckCircle size={22} style={{ color: BRAND_COLORS.primary }} />
            </div>
            <p className="text-sm font-medium" style={{ color: NEUTRAL_COLORS.black }}>
              {search || statusFilter !== 'all' ? 'No tasks match your filters' : 'No active tasks'}
            </p>
            <p className="text-xs" style={{ color: SYSTEM_COLORS.textSoft }}>
              {search || statusFilter !== 'all' ? 'Try adjusting your search or filter' : 'Tasks assigned to you will appear here'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {activeTasks.map(task => (
              <TaskCard
                key={`${task.projectId}-${task.id}`}
                task={task}
                onStatusChange={handleStatusChange}
              />
            ))}
          </div>
        )}
      </div>

      {/* Completed tasks */}
      {showDone && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: SYSTEM_COLORS.textSoft }}>
              Completed
            </p>
            <span className="text-xs" style={{ color: SYSTEM_COLORS.textSoft }}>{completedTasks.length} task{completedTasks.length !== 1 ? 's' : ''}</span>
          </div>

          {completedTasks.length === 0 ? (
            <p className="text-sm text-center py-8" style={{ color: SYSTEM_COLORS.textSoft }}>No completed tasks</p>
          ) : (
            <div className="space-y-2">
              {completedTasks.map(task => (
                <CompletedRow key={`${task.projectId}-${task.id}`} task={task} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Tasks;
